import { TrendingUp } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { AppShell } from '@/components/px/AppShell'
import { ConceptBadge } from '@/components/px/ConceptBadge'
import { DensityNote } from '@/components/px/DensityNote'
import { EmptyPanel } from '@/components/px/EmptyPanel'
import { ErrorPanel } from '@/components/px/ErrorPanel'
import { KpiCard } from '@/components/px/KpiCard'
import { ForbiddenPanel } from '@/components/px/ForbiddenPanel'
import { asApiError } from '@/services/apiError'
import { LoadingPanel } from '@/components/px/LoadingPanel'
import { SectionLabel } from '@/components/px/SectionLabel'
import { resourcesNav } from '@/components/px/nav'
import { selectActiveOrgId } from '@/features/auth/slices/sessionSlice'
import {
  analyticsErrorDismissed,
  fetchCycleTime,
  fetchVelocity,
} from '@/features/analytics/slices/analyticsSlice'
import {
  selectAnalyticsError,
  selectAnalyticsLoading,
  selectCycleTime,
  selectVelocity,
} from '@/features/analytics/selectors'
import { fetchProjects, selectProjectsItems } from '@/features/projects/slices/projectsSlice'
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  GROUP_ROW,
  MARGIN_MOVERS,
  OFFICE_ROWS,
  ORIGIN_SEGMENTS,
  POOL_HREF,
  UTILISATION_CHROME,
  UTILISATION_COPY,
  pct,
  perHour,
  type OfficeUtilisationRow,
  type OriginTone,
} from '@/demo/content/utilisation'
import { cn } from '@/lib/utils'
import { useAppDispatch, useAppSelector } from '@/store'

const copy = UTILISATION_COPY

const HEAD_CLASS = 'text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground'
const FIGURE_CLASS = 'v-figure tabular-nums text-foreground'

/**
 * Locked semantic key: aubergine = decided (near-shore mix, the plan the board
 * signed off), muted = the Western-EU remainder, mint = agent identity. Colour
 * never carries the meaning alone — every segment and legend entry is labelled.
 */
const SEGMENT_FILL: Record<OriginTone, string> = {
  nearshore: 'bg-primary text-primary-foreground',
  westernEu: 'bg-muted text-foreground',
  agent: 'bg-accent text-accent-foreground',
}

const SEGMENT_SWATCH: Record<OriginTone, string> = {
  nearshore: 'bg-primary',
  westernEu: 'bg-muted',
  agent: 'bg-accent',
}

function OfficeRow({ row }: { row: OfficeUtilisationRow }) {
  const { isTotal = false } = row

  return (
    <TableRow className={cn(isTotal && 'border-t-2 border-t-foreground/20 hover:bg-muted/50')}>
      <TableCell className="py-2.5">
        {isTotal ? (
          <span className="leading-tight">
            <span className="block font-semibold text-foreground">{row.label}</span>
            <span className="block text-xs text-muted-foreground">
              {copy.officeTable.totalRowLabel}
            </span>
          </span>
        ) : (
          <span className="leading-tight">
            <Link
              to={POOL_HREF}
              aria-label={copy.officeTable.officeLinkLabel(row.label)}
              className="block font-medium text-foreground underline-offset-2 hover:underline"
            >
              {row.label}
            </Link>
            <span className="block text-xs text-muted-foreground">{row.city}</span>
          </span>
        )}
      </TableCell>

      <TableCell className={cn(FIGURE_CLASS, 'text-right')}>{row.fte}</TableCell>

      <TableCell className={cn(FIGURE_CLASS, 'text-right', isTotal && 'font-semibold')}>
        {pct(row.billablePct)}
      </TableCell>

      <TableCell className="text-sm text-muted-foreground">{row.nonBillableSplit}</TableCell>

      <TableCell className={cn(FIGURE_CLASS, 'text-right')}>
        {perHour(row.loadedCostPerHour)}
      </TableCell>

      <TableCell className={cn(FIGURE_CLASS, 'text-right')}>
        {perHour(row.chargeRatePerHour)}
      </TableCell>

      <TableCell className={cn(FIGURE_CLASS, 'text-right font-semibold')}>
        {pct(row.marginPct)}
      </TableCell>
    </TableRow>
  )
}

/**
 * PX-W6.4 (AC-6): the LIVE section — charts of the series the project
 * analytics endpoints return, for a selected project.
 *
 * Velocity is `available:false` with a reason until Wave 4's board exists:
 * that branch renders the SERVER'S reason in an explained empty state — no
 * bars, no invented points. Cycle time renders the server's average (or its
 * "nothing decided" null) and the per-gate lines verbatim. Charts are
 * div/CSS bars in the deck's own visual language (I-8 — no new dependency).
 */
function UtilisationLiveSection() {
  const { t } = useTranslation('commercial')
  const dispatch = useAppDispatch()
  const orgId = useAppSelector(selectActiveOrgId)
  const projects = useAppSelector(selectProjectsItems)
  const velocity = useAppSelector(selectVelocity)
  const cycleTime = useAppSelector(selectCycleTime)
  const loading = useAppSelector(selectAnalyticsLoading)
  const error = useAppSelector(selectAnalyticsError)
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null)

  useEffect(() => {
    if (orgId === null) return
    void dispatch(fetchProjects(orgId))
  }, [dispatch, orgId])

  // The default selection is a pure projection of the loaded list — the
  // first readable project until the user picks one explicitly. Computed
  // during render instead of an Effect: no extra render round-trip, and no
  // cascading setState-in-effect.
  const projectId = selectedProjectId ?? (projects.length > 0 ? projects[0]!.id : null)

  useEffect(() => {
    if (projectId === null) return
    void dispatch(fetchVelocity(projectId))
    void dispatch(fetchCycleTime(projectId))
  }, [dispatch, projectId])

  const onRetryClicked = (): void => {
    if (projectId === null) return
    dispatch(analyticsErrorDismissed())
    void dispatch(fetchVelocity(projectId))
    void dispatch(fetchCycleTime(projectId))
  }

  // Bar heights are RELATIVE to the series' own maximum — a display scale,
  // never a derived business figure.
  //
  // ⚠ `points` is DESTRUCTURED WITH A DEFAULT, not optional-chained once and
  // then dereferenced: `velocity?.points.reduce(…)` guards a null `velocity`
  // but still throws on a velocity payload that carries no `points` array,
  // which is what an unavailable series looks like on the wire. The C2
  // fix-back's wider error coverage rendered exactly that shape and the page
  // crashed the whole tree.
  const { points: velocityPoints = [] } = velocity ?? {}
  const maxCompleted = velocityPoints.reduce((max, point) => Math.max(max, point.completed), 0)

  return (
    <section className="mt-7" aria-label={t('utilisation.live.title')} data-source="live" data-testid="utilisation-live">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
        <SectionLabel>{t('utilisation.live.title')}</SectionLabel>
        {projects.length > 0 && (
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className="sr-only">{t('utilisation.live.project')}</span>
            <select
              aria-label={t('utilisation.live.project')}
              value={projectId ?? ''}
              onChange={(e) => setSelectedProjectId(e.target.value)}
              className="rounded-md border border-input bg-card px-2 py-1 text-xs text-foreground"
            >
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      {projects.length === 0 ? (
        <div className="mt-2">
          <EmptyPanel
            title={t('utilisation.live.noProjects.title')}
            body={t('utilisation.live.noProjects.body')}
          />
        </div>
      ) : loading ? (
        <div className="mt-2">
          <LoadingPanel />
        </div>
      ) : error !== null && error.status === 403 ? (
        <div className="mt-2">
          {/* AC-6.2: the server refused the delivery analytics. */}
          <ForbiddenPanel requested="/{orgId}/projects/:projectId/analytics" />
        </div>
      ) : error !== null ? (
        <div className="mt-2">
          <ErrorPanel error={asApiError(error)} onRetryClicked={onRetryClicked} />
        </div>
      ) : (
        <div className="mt-2 grid gap-4 lg:grid-cols-2">
          {/* ── Velocity: the returned series, or the server's reason ────── */}
          <div className="rounded-lg border border-border bg-card px-4 py-3.5" aria-label={t('utilisation.live.velocity.title')}>
            <SectionLabel>{t('utilisation.live.velocity.title')}</SectionLabel>
            {velocity === null ? (
              <p className="mt-2 text-sm text-muted-foreground">—</p>
            ) : !velocity.available ? (
              <div className="mt-2">
                <EmptyPanel
                  title={t('utilisation.live.velocity.unavailable')}
                  body={velocity.reason ?? t('utilisation.live.velocity.noReason')}
                />
              </div>
            ) : velocityPoints.length === 0 ? (
              <p className="mt-2 text-sm text-muted-foreground">
                {t('utilisation.live.velocity.noPoints')}
              </p>
            ) : (
              <div className="mt-3 flex h-28 items-end gap-2" role="img" aria-label={t('utilisation.live.velocity.barsLabel')}>
                {velocityPoints.map((point, index) => (
                  <div key={index} className="flex h-full flex-1 flex-col justify-end">
                    <div
                      className="rounded-t bg-accent"
                      style={{ height: `${maxCompleted === 0 ? 0 : Math.round((point.completed / maxCompleted) * 100)}%` }}
                      title={t('utilisation.live.velocity.barTitle', {
                        completed: point.completed,
                        start: new Date(point.periodStart).toISOString().slice(0, 10),
                        end: new Date(point.periodEnd).toISOString().slice(0, 10)
                      })}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── Gate cycle time — the server's figures verbatim ──────────── */}
          <div className="rounded-lg border border-border bg-card px-4 py-3.5" aria-label={t('utilisation.live.cycleTime.title')}>
            <SectionLabel>{t('utilisation.live.cycleTime.title')}</SectionLabel>
            {cycleTime === null ? (
              <p className="mt-2 text-sm text-muted-foreground">—</p>
            ) : (
              <>
                <dl className="mt-2 grid grid-cols-2 gap-x-6 gap-y-2 text-sm lg:grid-cols-4">
                  <div>
                    <dt className="text-xs uppercase tracking-[0.06em] text-muted-foreground">
                      {t('utilisation.live.cycleTime.avg')}
                    </dt>
                    <dd className="v-figure mt-0.5 font-serif text-lg font-semibold tabular-nums text-foreground">
                      {cycleTime.avgGateCycleHours === undefined ||
                      cycleTime.avgGateCycleHours === null
                        ? t('utilisation.live.cycleTime.nothingDecided')
                        : `${cycleTime.avgGateCycleHours.toFixed(1)} h`}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase tracking-[0.06em] text-muted-foreground">
                      {t('utilisation.live.cycleTime.total')}
                    </dt>
                    <dd className="mt-0.5 text-foreground">{cycleTime.gatesTotal}</dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase tracking-[0.06em] text-muted-foreground">
                      {t('utilisation.live.cycleTime.closed')}
                    </dt>
                    <dd className="mt-0.5 text-foreground">{cycleTime.gatesClosed}</dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase tracking-[0.06em] text-muted-foreground">
                      {t('utilisation.live.cycleTime.open')}
                    </dt>
                    <dd className="mt-0.5 text-foreground">{cycleTime.gatesOpen}</dd>
                  </div>
                </dl>
                <ul className="mt-3 divide-y divide-border">
                  {/* Destructured with a default, like `points` above: a cycle-time
                      payload that carries no `gates` array crashed the tree. */}
                  {(cycleTime.gates ?? []).map((gate) => (
                    <li key={gate.ref} className="flex flex-wrap items-baseline justify-between gap-x-3 py-1.5 text-sm">
                      <span className="text-foreground">
                        {gate.title}
                        {gate.open && (
                          <span className="ml-2 rounded-full bg-review-bg px-2 py-0.5 text-[0.625rem] font-medium text-review">
                            {t('utilisation.live.cycleTime.openTag')}
                          </span>
                        )}
                      </span>
                      <span className="v-figure tabular-nums text-muted-foreground">
                        {gate.cycleHours === undefined || gate.cycleHours === null
                          ? '—'
                          : `${gate.cycleHours.toFixed(1)} h`}
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>
      )}
    </section>
  )
}

/**
 * 1.6 Utilisation & billability — the board's read on billable output and where
 * the margin comes from. The LIVE section (W6.4) renders the project analytics
 * series; the deck figures resolve through `@/demo/content/utilisation` and
 * stay visibly demo-badged in their own section below — no demo number is
 * presented as live.
 */
export default function UtilisationPage() {
  const { t } = useTranslation('commercial')
  const originDescription = ORIGIN_SEGMENTS.map(
    (segment) => `${segment.label}${segment.link ? ` ${segment.link.text}` : ''} ${pct(segment.sharePct)}`
  ).join(', ')

  return (
    <AppShell
      moduleLabel={UTILISATION_CHROME.moduleLabel}
      breadcrumb={UTILISATION_CHROME.breadcrumb}
      // Was the seed's 'Q3 2026' — a claim about time on a live-spine page.
      contextPill={t('utilisation.chrome.contextPill')}
      nav={resourcesNav}
    >
      <header className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
        <h1 className="font-serif text-2xl font-semibold">{copy.title}</h1>
        <p className="text-sm text-muted-foreground">{copy.lede}</p>
      </header>

      <UtilisationLiveSection />

      {/* ── Demo deck band: fixture figures, visibly badged (AC-6) ────────── */}
      <section className="mt-7" data-source="demo" data-testid="utilisation-demo" aria-label={t('utilisation.demoBand.title')}>
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <ConceptBadge />
          <p className="max-w-2xl text-xs text-muted-foreground">{t('utilisation.demoBand.note')}</p>
        </div>

        <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
          <KpiCard
            label={copy.kpis.billable.label}
            figure={copy.kpis.billable.figure}
            qualifier={copy.kpis.billable.qualifier}
          />
          <KpiCard
            label={copy.kpis.rate.label}
            figure={copy.kpis.rate.figure}
            qualifier={copy.kpis.rate.qualifier}
          />
          <KpiCard
            label={copy.kpis.cost.label}
            figure={copy.kpis.cost.figure}
            qualifier={copy.kpis.cost.qualifier}
          />
          <KpiCard
            label={copy.kpis.margin.label}
            figure={copy.kpis.margin.figure}
            qualifier={copy.kpis.margin.qualifier}
          />
        </div>

        <section className="mt-7" aria-label={copy.officeTable.heading}>
          <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
            <SectionLabel>{copy.officeTable.heading}</SectionLabel>
            <span className="text-xs text-muted-foreground">{copy.officeTable.unitNote}</span>
          </div>

          <div className="mt-2 rounded-lg border border-border bg-card">
            <Table>
              <TableCaption className="sr-only">{copy.officeTable.caption}</TableCaption>
              <TableHeader>
                <TableRow>
                  <TableHead className={HEAD_CLASS}>{copy.officeTable.columns.office}</TableHead>
                  <TableHead className={cn(HEAD_CLASS, 'text-right')}>
                    {copy.officeTable.columns.fte}
                  </TableHead>
                  <TableHead className={cn(HEAD_CLASS, 'text-right')}>
                    {copy.officeTable.columns.billable}
                  </TableHead>
                  <TableHead className={HEAD_CLASS}>
                    {copy.officeTable.columns.nonBillable}
                  </TableHead>
                  <TableHead className={cn(HEAD_CLASS, 'text-right')}>
                    {copy.officeTable.columns.cost}
                  </TableHead>
                  <TableHead className={cn(HEAD_CLASS, 'text-right')}>
                    {copy.officeTable.columns.rate}
                  </TableHead>
                  <TableHead className={cn(HEAD_CLASS, 'text-right')}>
                    {copy.officeTable.columns.margin}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {OFFICE_ROWS.map((row) => (
                  <OfficeRow key={row.key} row={row} />
                ))}
              </TableBody>
              <TableFooter className="bg-secondary">
                <OfficeRow row={GROUP_ROW} />
              </TableFooter>
            </Table>
          </div>
        </section>

        <section className="mt-7" aria-label={copy.origin.heading}>
          <SectionLabel>{copy.origin.heading}</SectionLabel>

          <div
            role="img"
            aria-label={copy.origin.barLabel(originDescription)}
            className="mt-2 flex h-11 overflow-hidden rounded-md border border-border"
          >
            {ORIGIN_SEGMENTS.map((segment) => (
              <div
                key={segment.key}
                style={{ width: `${segment.sharePct}%` }}
                className={cn(
                  'flex items-center justify-center border-r border-border last:border-r-0',
                  SEGMENT_FILL[segment.key]
                )}
              >
                <span className="v-figure text-sm font-semibold tabular-nums">
                  {pct(segment.sharePct)}
                </span>
              </div>
            ))}
          </div>

          <ul className="mt-2 flex flex-wrap gap-x-6 gap-y-1.5">
            {ORIGIN_SEGMENTS.map((segment) => (
              <li key={segment.key} className="flex items-center gap-2 text-sm text-ink-soft">
                <span
                  aria-hidden
                  className={cn(
                    'h-2.5 w-2.5 shrink-0 rounded-[2px] border border-border',
                    SEGMENT_SWATCH[segment.key]
                  )}
                />
                <span>
                  {segment.label}
                  {segment.link && (
                    <>
                      {' '}
                      <Link
                        to={segment.link.to}
                        className="text-foreground underline underline-offset-2 hover:text-accent-foreground"
                      >
                        {segment.link.text}
                      </Link>
                    </>
                  )}
                </span>
                <span className={cn(FIGURE_CLASS, 'text-sm font-semibold')}>
                  {pct(segment.sharePct)}
                </span>
              </li>
            ))}
          </ul>

          <p className="mt-2 text-sm text-muted-foreground">{copy.origin.verbatim}</p>
        </section>

        <section
          className="mt-7 rounded-lg border border-border bg-accent px-4 py-3.5"
          aria-label={copy.movers.heading}
        >
          <div className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-accent-foreground" aria-hidden />
            <SectionLabel className="text-accent-foreground">{copy.movers.heading}</SectionLabel>
          </div>
          <p className="mt-1 text-sm text-accent-foreground">{copy.movers.lede}</p>

          <ul className="mt-3 grid gap-2 md:grid-cols-3">
            {MARGIN_MOVERS.map((mover) => (
              <li key={mover.key} className="rounded-md border border-border bg-card px-3 py-2.5">
                <div className="text-sm text-foreground">{mover.what}</div>
                <div className="v-figure mt-0.5 font-serif text-lg font-semibold tabular-nums text-foreground">
                  {mover.result}
                </div>
                <Link
                  to={mover.to}
                  className="mt-1 inline-block text-xs text-accent-foreground underline underline-offset-2"
                >
                  {mover.linkLabel}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      </section>

      <DensityNote>{copy.densityNote}</DensityNote>
    </AppShell>
  )
}
